import type {
  ConductorTask,
  SwitchTask
} from 'Automations/nodeDefinitions/conductor'
import type { SimulationExpression } from '../../simulation.types'

export type SwitchBranch = {
  tasks: ConductorTask[]
  expression: SimulationExpression
}

/**
 * Decision cases in key order, then the default case. Branch priority is that
 * order, so ELSE — added last — is always the lowest.
 */
export const switchBranches = (switchTask: SwitchTask): SwitchBranch[] => {
  const cases = Object.entries(switchTask.decisionCases)
  const decisionCaseKeys = cases.map(([groupId]) => groupId)

  return [
    ...cases.map(([groupId, tasks], order) => ({
      tasks,
      expression: pathExpression({
        switchTask,
        decisionCaseKeys,
        result: groupId,
        order
      })
    })),
    {
      tasks: switchTask.defaultCase,
      // The expression returns "UNKNOWN" when no group matched, at which point
      // Conductor falls through to the default case.
      expression: pathExpression({
        switchTask,
        decisionCaseKeys,
        result: undefined,
        order: decisionCaseKeys.length
      })
    }
  ]
}

const pathExpression = ({
  switchTask,
  decisionCaseKeys,
  result,
  order
}: {
  switchTask: SwitchTask
  decisionCaseKeys: string[]
  result: string | undefined
  order: number
}): SimulationExpression => ({
  switchTaskReferenceName: switchTask.taskReferenceName,
  expression: switchTask.expression,
  switchInputParameters: stringValuedEntries(switchTask.inputParameters),
  result,
  decisionCaseKeys,
  order,
  isValueParam: switchTask.evaluatorType === 'value-param'
})

const stringValuedEntries = (
  inputParameters: Record<string, unknown>
): Record<string, string> =>
  Object.entries(inputParameters).reduce<Record<string, string>>(
    (acc, [key, value]) =>
      typeof value === 'string' ? { ...acc, [key]: value } : acc,
    {}
  )
